import React, { useEffect, useState } from 'react';
import {
  Badge, Button, DataTable, IdCell, Input, Modal, PageHead, SearchBox, Select,
  StatCard, Textarea, dateStr, num, titleCase,
} from '../components/ui';
import useList from '../hooks/useList';
import { userApi } from '../api';
import { useToast } from '../context/ToastContext';

const ROLES = ['buyer', 'manufacturer', 'distributor', 'supplier', 'logistics', 'insurance', 'finance', 'superadmin'];

const emptyForm = { name: '', mobile: '', email: '', role: 'buyer', password: '', designation: '' };

const Users = () => {
  const toast = useToast();
  const list = useList(userApi.list, { role: '', search: '', isBlocked: '' });

  const [stats, setStats] = useState(null);
  const [form, setForm] = useState(null);
  const [blockTarget, setBlockTarget] = useState(null);
  const [blockReason, setBlockReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    userApi.stats().then(res => setStats(res.data)).catch(() => {});
  }, []);

  const submitForm = async () => {
    if (!form.name || !form.mobile) return toast.error('Name and mobile number are required');

    setBusy(true);
    try {
      if (form._id) {
        await userApi.update(form._id, form);
        toast.success('User updated');
      } else {
        await userApi.create(form);
        toast.success('User created');
      }
      setForm(null);
      list.reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  const toggleBlock = async () => {
    setBusy(true);
    try {
      await userApi.block(blockTarget._id, { isBlocked: !blockTarget.isBlocked, reason: blockReason });
      toast.success(blockTarget.isBlocked ? 'Account restored' : 'Account blocked');
      setBlockTarget(null);
      setBlockReason('');
      list.reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'User',
      render: row => <IdCell image={row.avatar} name={row.name || 'Unnamed'} sub={`+91 ${row.mobile}`} />,
    },
    { key: 'email', label: 'Email', render: row => row.email || <span className="text-muted">—</span> },
    { key: 'role', label: 'Role', render: row => <Badge status={row.role} tone="brand" dot={false} /> },
    {
      key: 'company',
      label: 'Company',
      render: row => (row.company ? (
        <>
          <div>{row.company.name}</div>
          <div className="cell-muted mono">{row.company.code}</div>
        </>
      ) : <span className="text-muted">—</span>),
    },
    { key: 'createdAt', label: 'Joined', render: row => <span className="text-sm">{dateStr(row.createdAt)}</span> },
    {
      key: 'status',
      label: 'Status',
      render: row => (row.isBlocked ? <Badge status="blocked" /> : <Badge status="active" />),
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: row => (
        <div className="row-actions">
          <Button size="sm" variant="ghost" onClick={() => setForm({ ...row, password: '' })}>Edit</Button>
          {row.role !== 'superadmin' && (
            <Button
              size="sm"
              variant={row.isBlocked ? 'soft' : 'danger'}
              onClick={() => { setBlockTarget(row); setBlockReason(''); }}
            >
              {row.isBlocked ? 'Restore' : 'Block'}
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHead
        title="Users"
        subtitle="Every account on the platform, across all partner roles."
        actions={<Button icon="＋" onClick={() => setForm({ ...emptyForm })}>Add user</Button>}
      />

      {stats && (
        <div className="stat-grid stagger mb-2">
          <StatCard accent label="Total accounts" value={num(stats.total)} icon="⚇" foot={`${num(stats.newThisMonth)} joined this month`} />
          <StatCard label="Buyers" value={num(stats.byRole.buyer)} icon="🛒" />
          <StatCard label="Manufacturers" value={num(stats.byRole.manufacturer)} icon="🏭" />
          <StatCard label="Distributors" value={num(stats.byRole.distributor)} icon="🚛" />
          <StatCard label="Partners" value={num(stats.byRole.logistics + stats.byRole.insurance + stats.byRole.finance)} icon="🤝" foot="Logistics · Insurance · Finance" />
          <StatCard label="Blocked" value={num(stats.blocked)} icon="⛔" />
        </div>
      )}

      <div className="filters">
        <SearchBox value={list.filters.search} onChange={v => list.setFilter('search', v)} placeholder="Search name, mobile or email…" />
        <select className="select" style={{ width: 180 }} value={list.filters.role} onChange={e => list.setFilter('role', e.target.value)}>
          <option value="">All roles</option>
          {ROLES.map(r => <option key={r} value={r}>{titleCase(r)}</option>)}
        </select>
        <select className="select" style={{ width: 150 }} value={list.filters.isBlocked} onChange={e => list.setFilter('isBlocked', e.target.value)}>
          <option value="">Any status</option>
          <option value="false">Active</option>
          <option value="true">Blocked</option>
        </select>
      </div>

      <DataTable columns={columns} rows={list.rows} loading={list.loading} meta={list.meta} onPage={list.setPage} />

      <Modal
        open={!!form}
        title={form?._id ? 'Edit user' : 'Add a user'}
        subtitle={form?._id ? form.mobile : 'Create a partner or panel account'}
        onClose={() => setForm(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setForm(null)}>Cancel</Button>
            <Button loading={busy} onClick={submitForm}>{form?._id ? 'Save changes' : 'Create user'}</Button>
          </>
        }
      >
        {form && (
          <>
            <Input label="Full name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Rajesh Kumar" />
            <Input
              label="Mobile number" lead="+91" maxLength={10} disabled={!!form._id}
              value={form.mobile}
              onChange={e => setForm({ ...form, mobile: e.target.value.replace(/\D/g, '') })}
              hint={form._id ? 'The login number cannot be changed.' : 'This is how the user signs in.'}
            />
            <Input label="Email" type="email" value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} />
            <Select
              label="Role" value={form.role}
              onChange={e => setForm({ ...form, role: e.target.value })}
              options={ROLES.map(r => ({ value: r, label: titleCase(r) }))}
            />
            <Input label="Designation" value={form.designation || ''} onChange={e => setForm({ ...form, designation: e.target.value })} placeholder="Director" />
            {!form._id && (
              <Input
                label="Panel password (optional)" type="password"
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                hint="Only needed for web-panel logins. Mobile users sign in with OTP."
              />
            )}
          </>
        )}
      </Modal>

      <Modal
        open={!!blockTarget}
        title={blockTarget?.isBlocked ? 'Restore this account?' : 'Block this account?'}
        subtitle={blockTarget?.name}
        onClose={() => setBlockTarget(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setBlockTarget(null)}>Cancel</Button>
            <Button variant={blockTarget?.isBlocked ? 'primary' : 'danger'} loading={busy} onClick={toggleBlock}>
              {blockTarget?.isBlocked ? 'Restore access' : 'Block account'}
            </Button>
          </>
        }
      >
        <p className="text-muted mb-2">
          {blockTarget?.isBlocked
            ? 'The user will be able to sign in again immediately.'
            : 'The user is signed out everywhere and cannot log in until restored.'}
        </p>
        {!blockTarget?.isBlocked && (
          <Textarea label="Reason (sent to the user)" value={blockReason} onChange={e => setBlockReason(e.target.value)} placeholder="Repeated fraudulent claims" />
        )}
      </Modal>
    </>
  );
};

export default Users;
